import { onBeforeUnmount, onMounted, reactive, ref } from 'vue'
import { defaultFilters, hasChannels, markets, switchMarket, toQuery } from './model'
import type {
  Market,
  PayChannel,
  SettlementFilters,
  SettlementProduct,
  SettlementQuery,
  SettlementResponse,
  SettlementRow
} from '@/types/settlement'

export interface SettlementApi {
  list(query: SettlementQuery): Promise<SettlementResponse<SettlementRow[]>>
  products(query: { market: Market; scope: string; keyword?: string }): Promise<SettlementResponse<SettlementProduct[]>>
  payChannels(market: Market): Promise<SettlementResponse<PayChannel[]>>
  createExport(query: SettlementQuery): Promise<Blob>
}
interface Effects {
  success(text: string): void
  error(text: string): void
  save(blob: Blob, name: string): void
}
export function checked<T>(response: SettlementResponse<T>): T {
  if (!response || !['0', '0000'].includes(String(response.code))) throw new Error(response?.msg || '请求失败，请重试')
  return response.data
}
const errorText = (error: unknown) => (error instanceof Error ? error.message : '请求失败，请重试')

// CHECK-210：请求序号隔离市场和筛选状态；完整 Promise 控制 loading。
export function useSettlement(api: SettlementApi, effects: Effects) {
  let stored: string | null = null
  try {
    stored = localStorage.getItem('productSettlement.market')
  } catch {
    /* 浏览器禁用存储时仍可查询。 */
  }
  const market = markets.find((item) => item.key === stored)?.key || 'gp'
  const filters = reactive(defaultFilters(market))
  const rows = ref<SettlementRow[]>([])
  const products = ref<SettlementProduct[]>([])
  const channels = ref<PayChannel[]>([])
  const count = ref(0)
  const loading = ref(false)
  const exporting = ref(false)
  let listSequence = 0
  let productSequence = 0
  let channelSequence = 0
  let exportSequence = 0
  let active = true

  function cancelExport() {
    exportSequence++
    exporting.value = false
  }
  async function fetchList() {
    const sequence = ++listSequence
    loading.value = true
    try {
      const response = await api.list(toQuery(filters))
      if (!active || sequence !== listSequence) return
      rows.value = checked(response)
      count.value = response.count || 0
    } catch (error) {
      if (active && sequence === listSequence) {
        rows.value = []
        count.value = 0
        effects.error(errorText(error))
      }
    } finally {
      if (active && sequence === listSequence) loading.value = false
    }
  }
  async function searchProducts(keyword = '') {
    const sequence = ++productSequence
    try {
      const response = await api.products({ market: filters.market, scope: 'all', keyword })
      if (active && sequence === productSequence) products.value = checked(response)
    } catch (error) {
      if (active && sequence === productSequence) {
        products.value = []
        effects.error(errorText(error))
      }
    }
  }
  async function loadChannels() {
    const sequence = ++channelSequence
    channels.value = []
    if (!hasChannels(filters.market)) return
    try {
      const response = await api.payChannels(filters.market)
      if (active && sequence === channelSequence) channels.value = checked(response)
    } catch (error) {
      if (active && sequence === channelSequence) effects.error(errorText(error))
    }
  }
  function changeFilters(patch: Partial<SettlementFilters>) {
    cancelExport()
    Object.assign(filters, patch, { pageNum: 1 })
    rows.value = []
    count.value = 0
    void fetchList()
  }
  function changeMarket(next: Market) {
    cancelExport()
    for (const key of ['dbtIdList', 'payChnlList', 'dimensionList'] as const) delete filters[key]
    Object.assign(filters, switchMarket(filters, next))
    try {
      localStorage.setItem('productSettlement.market', next)
    } catch {
      /* 存储不可用不阻断市场切换。 */
    }
    rows.value = []
    count.value = 0
    products.value = []
    void fetchList()
    void searchProducts()
    void loadChannels()
  }
  function reset() {
    cancelExport()
    for (const key of ['dbtIdList', 'payChnlList', 'dimensionList'] as const) delete filters[key]
    Object.assign(filters, defaultFilters(filters.market))
    rows.value = []
    count.value = 0
    void fetchList()
    void searchProducts()
  }
  function changePage(page: number, size: number) {
    filters.pageNum = page
    filters.pageSize = size
    void fetchList()
  }
  // CHECK-209：导出请求持有筛选快照，切换/卸载后迟到响应不保存文件。
  async function exportFile() {
    if (exporting.value) return
    const sequence = ++exportSequence
    const query = toQuery(filters)
    const current = () => active && sequence === exportSequence
    exporting.value = true
    try {
      const blob = await api.createExport(query)
      if (!current()) return
      if (!(blob instanceof Blob) || !blob.size || /json|html|^text\//i.test(blob.type))
        throw new Error('下载文件无效，请重试')
      effects.save(blob, `产品结算-${query.market}.xlsx`)
      effects.success('导出成功')
    } catch (error) {
      if (current()) effects.error(errorText(error))
    } finally {
      if (current()) exporting.value = false
    }
  }
  onMounted(() => {
    void fetchList()
    void searchProducts()
    void loadChannels()
  })
  onBeforeUnmount(() => {
    active = false
    listSequence++
    productSequence++
    channelSequence++
    cancelExport()
  })
  return {
    filters,
    rows,
    products,
    channels,
    count,
    loading,
    exporting,
    fetchList,
    searchProducts,
    changeFilters,
    changeMarket,
    changePage,
    reset,
    exportFile
  }
}
